import { useMemo, useState } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Badge,
  Card,
  Avatar,
  AvatarFallback,
  Separator,
  RadioGroup,
  RadioGroupItem,
  Label,
  Input,
  Button,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Checkbox,
} from "@pushpress/pushpress-ui";
import { Loader2, Search, Users } from "lucide-react";
import { toast } from "react-toastify";
import { useDebounce } from "react-use";
import {
  defaultToastOptions,
  getInitials,
  joinStrings,
} from "../../../utils/misc";
import { useSendEmailToMembers } from "../api/migrationAssistantApi.hooks";
import { getMemberPayment, shouldShowMember } from "../helpers";
import { MemberDataType } from "../types";
import { useExperiment } from "../../../hooks/useExperiment";
import { SHOW_MEMBER_PROFILE_FEATURE_FLAG } from "../../../context/features/features.utils";

type FilterType = "all" | "has-payment" | "payment-requested";

export default function ActivatedTabView({
  membersData,
}: {
  membersData: MemberDataType[];
}) {
  const { mutate: sendEmailToMembers, isLoading } = useSendEmailToMembers();
  const [filter, setFilter] = useState<FilterType>("all");
  const [selectedMembers, setSelectedMembers] = useState<Set<number>>(
    new Set(),
  );

  const [keyword, setKeyword] = useState<string>("");
  const [keywordDebounced, setKeywordDebounced] = useState<string>("");

  useDebounce(
    () => {
      setKeywordDebounced(keyword);
    },
    300,
    [keyword],
  );

  const data = useMemo(() => {
    return membersData
      .filter((member) => {
        if (filter === "all") {
          return true;
        }
        if (filter === "has-payment") {
          return member.billingBrand && member.billingLastFour;
        }
        if (filter === "payment-requested") {
          return (
            !member.billingBrand &&
            !member.billingLastFour &&
            member.collectPayment
          );
        }
        return true;
      })
      .filter((member) => {
        return shouldShowMember(member, keywordDebounced);
      })
      .sort((a, b) => {
        return a.firstName.localeCompare(b.firstName);
      });
  }, [membersData, filter, keywordDebounced]);

  if (membersData.length === 0) {
    return (
      <Card>
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-space-y-2 tw-p-16">
          <Users className="tw-h-8 tw-w-8 tw-text-muted-foreground" />
          <p className="tw-text-lg tw-font-medium">No members yet</p>
          <p className="tw-text-sm tw-text-muted-foreground">
            It looks like you haven&apos;t added any members to your table.
          </p>
        </div>
      </Card>
    );
  }

  const handleSelectMember = (id: number) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedMembers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedMembers.size === data.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(data.map((member) => member.id)));
    }
  };

  const onFilterChange = (value: FilterType) => {
    setFilter(value);
    setSelectedMembers(new Set());
  };

  const handleSendAction = () => {
    if (selectedMembers.size === 0) {
      return;
    }
    sendEmailToMembers(Array.from(selectedMembers), {
      onSuccess: () => {
        toast.success("Emails sent", defaultToastOptions);
      },
      onError: () => {
        toast.error("There was an error sending emails", defaultToastOptions);
      },
    });
    setSelectedMembers(new Set());
  };

  const getBillingInfo = (member: MemberDataType) => {
    if (member.billingBrand && member.billingLastFour) {
      return getMemberPayment(member);
    }
    if (member.collectPayment) {
      return (
        <div className="tw-flex tw-items-center tw-gap-3">
          <Badge variant="secondary" className="tw-text-slate-600">
            REQUESTED
          </Badge>
        </div>
      );
    }
    return <div className="tw-flex tw-items-center">––</div>;
  };

  return (
    <>
      {isLoading && (
        <div className="tw-flex tw-justify-center tw-items-center tw-mt-4">
          <Loader2 className="tw-h-6 tw-w-6 tw-animate-spin tw-text-blue-500" />
          <span className="tw-ml-2 tw-text-gray-500">Sending emails...</span>
        </div>
      )}
      {!isLoading && (
        <Card>
          <div className="tw-@container tw-p-6">
            <div className="tw-flex tw-flex-col tw-gap-4 @[880px]:tw-flex-row @[880px]:tw-items-center">
              <div className="tw-flex tw-flex-col tw-gap-4 tw-flex-1 @[680px]:tw-flex-row @[680px]:tw-items-center">
                <Input
                  type="text"
                  placeholder="Search by name"
                  className="!tw-w-[260px]"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
                <RadioGroup
                  className="!tw-flex tw-gap-2 tw-flex-wrap tw-flex-row !tw-flex-1 tw-text-slate-600 tw-ml-6"
                  value={filter}
                  onValueChange={(value: FilterType) => onFilterChange(value)}
                >
                  <div className="tw-flex tw-items-center tw-mr-4">
                    <RadioGroupItem value="all" id="r1" />
                    <Label htmlFor="r1" className="tw-mb-0 tw-pl-3">
                      All
                    </Label>
                  </div>
                  <div className="tw-flex tw-items-center tw-mr-4">
                    <RadioGroupItem value="has-payment" id="r2" />
                    <Label htmlFor="r2" className="tw-mb-0 tw-pl-3">
                      Payment on file
                    </Label>
                  </div>
                  <div className="tw-flex tw-items-center">
                    <RadioGroupItem value="payment-requested" id="r4" />
                    <Label htmlFor="r4" className="tw-mb-0 tw-pl-3">
                      Payment requested
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </div>
          <Separator />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="tw-w-[5%] tw-bg-transparent tw-pl-6 tw-pb-4 tw-pt-4">
                  <Checkbox
                    data-testid="select-all-checkbox"
                    checked={
                      selectedMembers.size === data.length && data.length > 0
                    }
                    onCheckedChange={() => handleSelectAll()}
                    aria-label="Select all members"
                  />
                </TableHead>
                <TableHead className="tw-w-[50%] tw-bg-transparent tw-pl-4">
                  {selectedMembers.size > 0
                    ? `${selectedMembers.size} selected`
                    : "Name"}
                </TableHead>
                <TableHead className="tw-bg-transparent tw-pl-4">
                  {selectedMembers.size > 0 ? (
                    <div className="tw-flex tw-items-center tw-justify-end tw-pr-8">
                      <SendEmailsConfirmationDialog
                        memberCount={selectedMembers.size}
                        handleSendAction={() => handleSendAction()}
                      />
                    </div>
                  ) : (
                    "Payment method"
                  )}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="tw-w-[5%] tw-font-medium tw-pl-6 tw-pt-3 tw-pb-3">
                    <Checkbox
                      checked={selectedMembers.has(member.id)}
                      onCheckedChange={() => handleSelectMember(member.id)}
                      aria-label={`Select ${member.firstName} ${member.lastName}`}
                    />
                  </TableCell>
                  <TableCell className="tw-w-[50%] tw-font-medium tw-pl-3 tw-pt-3 tw-pb-3">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Avatar>
                        <AvatarFallback className="tw-text-slate-600">
                          {getInitials(
                            member.firstName,
                            member.lastName,
                          ).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <MemberLink member={member} />
                    </div>
                  </TableCell>
                  <TableCell>{getBillingInfo(member)}</TableCell>
                </TableRow>
              ))}
              {data.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="tw-h-24 tw-text-center tw-p-16"
                  >
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-space-y-2 tw-p-16">
                      <Search className="tw-h-8 tw-w-8 tw-text-muted-foreground" />
                      <p className="tw-text-lg tw-font-medium">
                        No results found
                      </p>
                      <p className="tw-text-sm tw-text-muted-foreground">
                        Try adjusting your search term to find what you&apos;re
                        looking for.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
    </>
  );
}

function SendEmailsConfirmationDialog({
  memberCount,
  handleSendAction,
}: {
  memberCount: number;
  handleSendAction: () => void;
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline">Resend welcome email</Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Resend welcome email?</AlertDialogTitle>
          <AlertDialogDescription>
            This will resend the welcome email to {memberCount} member(s).
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => handleSendAction()}>
            Send
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function MemberLink({ member }: { member: MemberDataType }) {
  const { useExperimentValue } = useExperiment();
  const isMemberProfilePageEnabled = useExperimentValue(
    SHOW_MEMBER_PROFILE_FEATURE_FLAG,
    true,
  ) as boolean;

  const memberProfileHref = isMemberProfilePageEnabled
    ? `/user/${member.userUuid}`
    : `/members/view/${member.userUuid}`;

  return (
    <a
      data-testid="user-profile-button"
      href={member.userUuid ? memberProfileHref : "/people/members"}
      target="_blank"
      rel="noreferrer"
    >
      {`${joinStrings(member.firstName, member.lastName)}`}
    </a>
  );
}
